import { cn } from '@app/utils/cn';
import { isDefined } from '@app/utils/is';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SideProp } from 'react-laag/dist/Sides';
import { CSSTransition } from 'react-transition-group';

import { Button } from '../Button';
import { CancelSmall } from '../Icons';

interface Props {
  id?: string;
  className?: string;
  layerSide: SideProp;
  isOpen: boolean;
  style?: React.CSSProperties;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  title?: string;
  onClose?: () => void;
}

export const PopoverSheet = React.forwardRef<HTMLDivElement, React.WithChildren<Props>>(
  ({ id, children, layerSide, className, isOpen, title, onClose, ...rest }, ref) => {
    const { t } = useTranslation(['common']);

    return (
      <CSSTransition
        in={isOpen}
        timeout={150}
        unmountOnExit
        classNames={{
          enter: 'opacity-0 scale-90',
          enterActive: '!opacity-100 !scale-100 transition-[opacity,transform]',
          exit: 'opacity-100 scale-100',
          exitActive: '!opacity-0 !scale-90 transition-[opacity,transform]',
        }}
      >
        <div
          id={id}
          ref={ref}
          className={cn(
            'z-popover flex max-h-[70vh] max-w-[94vw] flex-col overflow-hidden rounded-regular border border-neutral-96 bg-white p-4 text-neutral-40 shadow-1 body-2',
            className,
          )}
          data-side={layerSide}
          {...rest}
        >
          {isDefined(title) && (
            <p data-component="PopoverSheetTitle" className="mb-4 break-all pr-10 text-neutral-20 heading-3">
              {title}
            </p>
          )}
          {isDefined(onClose) && (
            <Button
              aria-label={t('common:close')}
              color="secondary"
              variant="ghost"
              onClick={onClose}
              className="absolute right-3 top-3 z-modal"
              size="medium"
              icon={CancelSmall}
            />
          )}
          <div className="overflow-auto">{children}</div>
        </div>
      </CSSTransition>
    );
  },
);
